// Client for the Keyword Performance Dashboard.
//
// Its own endpoints, not the Search Terms ones: the two surfaces answer to
// different documents and must be able to change independently.
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const BASE = `${API_URL}/seller-intelligence/keyword-performance`;

const headers = (token) => ({
  Authorization: `Bearer ${token}`,
  'Content-Type': 'application/json',
});

export class NotConnectedError extends Error {
  constructor(message) {
    super(message || 'Amazon account not connected');
    this.notConnected = true;
  }
}

const call = async (url, options) => {
  const res = await fetch(url, options);
  const data = await res.json().catch(() => ({}));
  if (res.status === 409 && data.connected === false) throw new NotConnectedError(data.error);
  if (!res.ok || !data.success) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
};

const query = (params) => {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') search.set(k, v);
  });
  return search.toString();
};

// SF1-SF6 as six cards.
export const getOverview = (token, params) =>
  call(`${BASE}/overview?${query(params)}`, { headers: headers(token) });

// The table. `view` is the filter, `sort` is the ranking.
export const getTable = (token, params) =>
  call(`${BASE}/table?${query(params)}`, { headers: headers(token) });

// The second list — terms the seller gets no impressions on at all.
export const getAbsent = (token, params) =>
  call(`${BASE}/absent?${query(params)}`, { headers: headers(token) });

export const getCompetitorWins = (token, params) =>
  call(`${BASE}/competitor-wins?${query(params)}`, { headers: headers(token) });

// Row expansion: the per-product split and the volume trend behind a term.
export const getTerm = (token, term, params) =>
  call(`${BASE}/term/${encodeURIComponent(term)}?${query(params)}`,
       { headers: headers(token) });

// The PRD's edge case: a brand name inside a generic term is tagged branded,
// and the seller may untag it. The correction persists across periods.
export const reclassify = (token, searchTerm, label) =>
  call(`${BASE}/classification`, {
    method: 'PUT', headers: headers(token),
    body: JSON.stringify({ search_term: searchTerm, label }),
  });

// Only the products that have terms in this window. Offering the whole
// catalogue would send the seller to an empty table more often than not, and
// an empty table reads as a broken feature rather than as a product Amazon
// did not show in search.
export const getProducts = (token, params) =>
  call(`${BASE}/products?${query(params)}`, { headers: headers(token) });

// What changed about the seller's terms since the window before — the half of
// the alert pass this feature owns.
export const getChanges = (token, params) =>
  call(`${BASE}/changes?${query(params)}`, { headers: headers(token) });
